import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import AiConfiguration from "./ai-configuration";
import AiCampaignExpiry from "./ai-campaign-expiry";
import AiExecutionMode from "./ai-execution-mode";
import AiTraining from "./ai-training";
import { PiMagnifyingGlass } from "react-icons/pi";
import axios from "../../axios";
import apiError from "../../error";
import vishingImg from "../../assets/create_campaign/vishing.png";
import aivishingImg from "../../assets/create_campaign/aivishing.png";
import whatsappImgIVR from "../../assets/create_campaign/whatsapp-ivr.png";
import whatsappImgChatbox from "../../assets/create_campaign/chatbox.png";
import simulationandtrain from "../../assets/create_campaign/simulationandtrain.png";
import {
  emptyData,
  coerceBoolean,
  toNumber,
  toNumberArray,
  toDate,
  normalizeAttackType,
  normalizeSimulationType,
  normalizeInteractionType,
  normalizeSimulationTypeOption,
  formatAttackTypeLabel,
  getSelectedAttackTypeOption,
  isEmailTemplateFlow,
  getAttackTypeRules,
  getAwarenessTargetOptions,
  generateIdentifier,
  applyAttackVectorDefaults,
  buildDataFromSources,
  getTemplateIds,
  resolveCampaignExpiry,
  normalizeExpiryOption,
  resolveLmsMsgStatus,
  cleanPayload,
  sumGroupTargets,
  shouldShowTemplatePreview,
  shouldShowLandingPageTab,
  shouldShowAwarenessPageTab,
} from "./campaign-template-utils";
import "./campaign-draft-preview.css";

const PREVIEW_TAB = "Preview";
const TRAINING_TAB = "Training";

// Logical width the compiled HTML is rendered at before being CSS-scaled down
// to fit a thumbnail — gives a true "whole page, shrunk" preview rather than a
// zoomed-in top-left crop.
const HTML_THUMB_DESIGN_WIDTH = 1200;
const GROUP_PICKER_WIDTH = 320;
const GROUP_PICKER_MIN_WIDTH = 260;
const GROUP_PICKER_MARGIN = 12;
const GROUP_PICKER_ESTIMATED_HEIGHT = 410;

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

function HtmlThumb({ html, title }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(el);
    } else {
      window.addEventListener("resize", update);
    }
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  const scale = size.width ? size.width / HTML_THUMB_DESIGN_WIDTH : 0;
  const frameHeight = scale ? size.height / scale : 0;

  return (
    <div className="ec-html-thumb" ref={containerRef}>
      {scale > 0 && (
        <iframe
          className="ec-template-preview-frame"
          srcDoc={html}
          title={title}
          sandbox=""
          scrolling="no"
          style={{
            width: `${HTML_THUMB_DESIGN_WIDTH}px`,
            height: `${frameHeight}px`,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        />
      )}
    </div>
  );
}

function InlineCampaignDraftField({ value, onChange, inputProps = {}, disabled = false }) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <input
        className="ec-inline-input"
        value={value || ""}
        autoFocus
        disabled={disabled}
        onChange={(e) => {
          if (!disabled) onChange(e.target.value);
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
        {...inputProps}
      />
    );
  }

  return (
    <span
      className={`ec-info-value${disabled ? " disabled" : ""}`}
      onClick={() => {
        if (!disabled) setEditing(true);
      }}
      title={disabled ? "This campaign has been launched" : "Click to edit"}
      aria-disabled={disabled}
    >
      {value || "--"}
    </span>
  );
}

function TrainingSettings({ data, setData, awarenessTargetOptions, disabled = false }) {
  const attackType = normalizeAttackType(data.attackVector);
  const isAttachment = attackType === "Attachment";
  const showDelay = isAttachment || !coerceBoolean(data.isInstantAwareness, true);

  const update = (patch) => {
    if (disabled) return;
    setData((d) => ({ ...d, ...patch }));
  };

  return (
    <div className="ais-card">
      <p className="ais-title">Awareness Targeted Group:</p>

      <div className="ais-row">
        <span className="ais-label">Trigger</span>
        <select
          className="ec-meta-select"
          value={data.lmsMsgStatus || ""}
          onChange={(e) => update({ lmsMsgStatus: e.target.value })}
          disabled={disabled}
        >
          {awarenessTargetOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {!isAttachment && (
        <div className="ais-row">
          <span className="ais-label">Instant Awareness</span>
          <div className="ais-radio-row">
            <label className="ais-radio-label">
              <input
                type="radio"
                name="instant-awareness"
                checked={coerceBoolean(data.isInstantAwareness, true)}
                onChange={() => update({ isInstantAwareness: true, delay: 1 })}
                disabled={disabled}
              />
              Yes
            </label>
            <label className="ais-radio-label">
              <input
                type="radio"
                name="instant-awareness"
                checked={!coerceBoolean(data.isInstantAwareness, true)}
                onChange={() => update({ isInstantAwareness: false })}
                disabled={disabled}
              />
              No
            </label>
          </div>
        </div>
      )}

      {showDelay && (
        <div className="ais-row">
          <span className="ais-label">Select Time Frame</span>
          <select
            className="ec-meta-select"
            value={String(data.delay || 1)}
            onChange={(e) => update({ delay: e.target.value })}
            disabled={disabled}
          >
            <option value="1">1 sec</option>
            <option value="5">5 min</option>
            <option value="10">10 min</option>
            <option value="15">15 min</option>
          </select>
        </div>
      )}
    </div>
  );
}

const CampaignDraftPreview = ({ campaignId, onClose, onDraft, onRunCampaign, onViewCampaign, isRunningCampaign = false }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(emptyData);
  const [templates, setTemplates] = useState([]);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupOptionsLoading, setGroupOptionsLoading] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [pendingGroupDetails, setPendingGroupDetails] = useState([]);
  const [groupPickerStyle, setGroupPickerStyle] = useState(null);
  const groupPickerAnchorRef = useRef(null);
  const templateComboAnchorRef = useRef(null);
  const [isHydrating, setIsHydrating] = useState(false);
  const [attackTypeConditions, setAttackTypeConditions] = useState({});
  const [simulationTypeOptions, setSimulationTypeOptions] = useState([]);
  const [, setSimulationTypeOptionsLoading] = useState(false);

  // Selectable templates (filtered by attack type / credential harvesting / sim
  // type) and the rendered preview (email / landing / awareness) of the one
  // currently selected.
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [availableTemplatesLoading, setAvailableTemplatesLoading] = useState(false);
  // Previews are keyed by template id so multiple selected templates can each
  // render their own email / landing / awareness preview.
  const [templatePreviewMap, setTemplatePreviewMap] = useState({});
  const [templatePreviewLoadingIds, setTemplatePreviewLoadingIds] = useState({});
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  // The thumbnail currently expanded in the lightbox: { kind: "image"|"html", src?, html?, title }.
  const [expandedImage, setExpandedImage] = useState(null);
  // Per-card image-load failure flags, keyed by template id then card:
  // { [templateId]: { email, landing, awareness } }. Lets us fall back to the
  // compiled HTML when the rendered thumbnail image can't be loaded.
  const [imageFallback, setImageFallback] = useState({});

  const fetchAttackTypeConditions = useCallback(async () => {
    try {
      const response = await axios.get("/api/config/attack-type-conditions");
      const conditions = response.data?.attackTypeConditions;
      setAttackTypeConditions(
        conditions && typeof conditions === "object" && !Array.isArray(conditions)
          ? conditions
          : {}
      );
    } catch (err) {
      apiError(err);
      setAttackTypeConditions({});
    }
  }, []);

  const fetchSimulationTypeOptions = useCallback(async () => {
    setSimulationTypeOptionsLoading(true);
    try {
      const response = await axios.get("/api/simulation/type", {});
      const options = Array.isArray(response.data?.simulation_type)
        ? response.data.simulation_type.map(normalizeSimulationTypeOption).filter(Boolean)
        : [];
      setSimulationTypeOptions(options);
    } catch (err) {
      apiError(err);
      setSimulationTypeOptions([]);
    } finally {
      setSimulationTypeOptionsLoading(false);
    }
  }, []);

  const fetchCampaignPreview = useCallback(async () => {
    if (!campaignId) return;

    setIsHydrating(true);
    try {
      const draftResponse = await axios.get(`/api/simulation/draft-campaign/${encodeURIComponent(campaignId)}`);
      const payload = draftResponse.data?.data || {};
      const templateIds = getTemplateIds(payload);

      const [groupsResponse, templateDetailResponse, templatePreviews, awarenessPages] = await Promise.all([
        axios.get("/api/groups/parentGroup", {}),
        templateIds.length
          ? axios.get("/api/campaign/template/detail", {
              params: {
                type: "My Templates",
                templateId: templateIds,
              },
            })
          : Promise.resolve({ data: { campaign_templates: [] } }),
        Promise.all(
          templateIds.map((templateId) =>
            axios
              .get(`/api/campaign/template/preview/${templateId}`)
              .then((response) => response.data || {})
              .catch(() => ({}))
          )
        ),
        Promise.all(
          templateIds.map((templateId) =>
            axios
              .get("/api/campaign/template/awareness-page", {
                params: { campaignTemplateId: templateId },
              })
              .then((response) => response.data || {})
              .catch(() => ({}))
          )
        ),
      ]);

      const { data: nextData, templates: nextTemplates } = buildDataFromSources(
        payload,
        groupsResponse.data?.groups || [],
        templateDetailResponse.data?.campaign_templates || [],
        templatePreviews,
        awarenessPages
      );

      setData(nextData);
      setTemplates(nextTemplates);
    } catch (err) {
      apiError(err);
    } finally {
      setIsHydrating(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchAttackTypeConditions();
  }, [fetchAttackTypeConditions]);

  useEffect(() => {
    fetchSimulationTypeOptions();
  }, [fetchSimulationTypeOptions]);

  useEffect(() => {
    fetchCampaignPreview();
  }, [fetchCampaignPreview]);

  const selectedSimulationTypeOption = useMemo(() => {
    const simulationType = normalizeSimulationType(data.type);
    return simulationTypeOptions.find((option) => option.value === simulationType) || null;
  }, [data.type, simulationTypeOptions]);

  const visibleSimulationTypeOptions = useMemo(() => {
    const simulationType = normalizeSimulationType(data.type);
    if (!simulationTypeOptions.some((option) => option.value === simulationType)) {
      return [
        { id: null, value: simulationType, label: simulationType, attackTypeOptions: [] },
        ...simulationTypeOptions,
      ];
    }
    return simulationTypeOptions;
  }, [data.type, simulationTypeOptions]);

  const attackTypeOptions = selectedSimulationTypeOption?.attackTypeOptions || [];
  const visibleAttackTypeOptions = useMemo(() => {
    const attackType = normalizeAttackType(data.attackVector);
    const hasCurrentAttackType = attackTypeOptions.some((option) => normalizeAttackType(option.value) === attackType);
    if (!hasCurrentAttackType) {
      const rules = getAttackTypeRules(data, attackTypeConditions);
      return [
        {
          id: null,
          value: attackType,
          label: formatAttackTypeLabel(attackType),
          type: rules.type || "",
        },
        ...attackTypeOptions,
      ];
    }
    return attackTypeOptions;
  }, [attackTypeConditions, attackTypeOptions, data]);

  const selectedAttackTypeOption = useMemo(
    () => getSelectedAttackTypeOption(data, visibleAttackTypeOptions),
    [data, visibleAttackTypeOptions]
  );

  const isTrainingSimulation = normalizeSimulationType(data.type) === "Simulation Attack + Training";
  const visibleTabs = useMemo(() => {
    const tabs = [PREVIEW_TAB];
    if (isTrainingSimulation) tabs.push(TRAINING_TAB);
    return tabs;
  }, [isTrainingSimulation]);
  const activeTabName = visibleTabs[activeTab] || PREVIEW_TAB;
  const isAlreadyLaunched = data.launchStatus === "launched";
  const launchedCampaignId = data.launchedCampaignId;
  const isPreviewLocked = isAlreadyLaunched || isRunningCampaign;
  const awarenessTargetOptions = useMemo(() => getAwarenessTargetOptions(data), [data]);
  const isCredentialHarvestingAllowed = useMemo(
    () => !!getAttackTypeRules(data, attackTypeConditions).isCredentialHarvestingValid,
    [attackTypeConditions, data]
  );

  // ── Selectable-template list + per-template preview ──
  const selectedAttackTypeId = selectedAttackTypeOption?.id ?? null;
  const showEmailPreview = useMemo(
    () => shouldShowTemplatePreview(data, attackTypeConditions, selectedAttackTypeOption),
    [data, attackTypeConditions, selectedAttackTypeOption]
  );
  const showLandingPreview = useMemo(
    () => shouldShowLandingPageTab(data, attackTypeConditions),
    [data, attackTypeConditions]
  );
  const showAwarenessPreview = useMemo(
    () => shouldShowAwarenessPageTab(data, attackTypeConditions),
    [data, attackTypeConditions]
  );

  const fetchAvailableTemplates = useCallback(async () => {
    setAvailableTemplatesLoading(true);
    try {
      const response = await axios.get("/api/campaign/template", {
        params: {
          type: "My Templates",
          limit: 200,
          page: 1,
          search: "",
          attackTypeIds: selectedAttackTypeId ? [selectedAttackTypeId] : [],
          credentialHarvesting: coerceBoolean(data.credentialHarvesting, false),
          withTraining: isTrainingSimulation,
          targetInteractionType: normalizeInteractionType(data.targetInteractionType) || null,
          isAIbased: coerceBoolean(data.isAIbased, false),
        },
      });
      const list = Array.isArray(response.data?.campaign_templates)
        ? response.data.campaign_templates
        : [];
      setAvailableTemplates(
        list
          .map((tpl) => ({
            id: toNumber(tpl?.id ?? tpl?.campaign_template?.id),
            name: tpl?.name ?? tpl?.campaign_template?.name ?? `Template ${tpl?.id}`,
          }))
          .filter((tpl) => tpl.id != null)
      );
    } catch (err) {
      apiError(err);
      setAvailableTemplates([]);
    } finally {
      setAvailableTemplatesLoading(false);
    }
  }, [selectedAttackTypeId, data.credentialHarvesting, data.targetInteractionType, data.isAIbased, isTrainingSimulation]);

  const fetchTemplatePreview = useCallback(async (templateId) => {
    const id = toNumber(templateId);
    if (id == null) return;
    setTemplatePreviewLoadingIds((prev) => ({ ...prev, [id]: true }));
    setImageFallback((prev) => ({ ...prev, [id]: { email: false, landing: false, awareness: false } }));
    try {
      const [previewResponse, awarenessResponse, detailResponse] = await Promise.all([
        axios.get(`/api/campaign/template/preview/${id}`),
        axios
          .get("/api/campaign/template/awareness-page", {
            params: { campaignTemplateId: id },
          })
          .then((r) => r.data || {})
          .catch(() => ({})),
        // The preview endpoint omits the raw email/landing HTML (it only returns
        // rendered thumbnail images), so fetch the template detail to get the
        // compiled HTML used as the image fallback.
        axios
          .get("/api/campaign/template/detail", {
            params: { type: "My Templates", templateId: [id] },
          })
          .then((r) => r.data || {})
          .catch(() => ({})),
      ]);
      const res = previewResponse.data || {};
      const campaignTemplate = res.campaign_template || {};
      const detailList = Array.isArray(detailResponse.campaign_templates)
        ? detailResponse.campaign_templates
        : [];
      const detailCT = (detailList[0] && detailList[0].campaign_template) || {};
      const preview = {
        id,
        name: campaignTemplate.name || detailCT.name || "",
        templatePath: res.templatePath || "",
        pagePath: res.pagePath || "",
        awarenessPath: awarenessResponse.awarenessPath || res.awarenessPath || "",
        awarenessUrl: res.awarenessUrl || awarenessResponse.url || "",
        landingUrl: campaignTemplate.page?.landing_url || detailCT.page?.landing_url || "",
        // Compiled HTML kept as a fallback when the rendered thumbnail image is
        // missing or fails to load.
        emailHtml: detailCT.template?.html || campaignTemplate.template?.html || "",
        landingHtml: detailCT.page?.html || campaignTemplate.page?.html || "",
        awarenessHtml: awarenessResponse.html || "",
        awarenessMode: awarenessResponse.mode || "",
      };
      setTemplatePreviewMap((prev) => ({ ...prev, [id]: preview }));
    } catch (err) {
      apiError(err);
      // Store a minimal entry so the fetch is not retried indefinitely.
      setTemplatePreviewMap((prev) => ({ ...prev, [id]: prev[id] || { id, name: "" } }));
    } finally {
      setTemplatePreviewLoadingIds((prev) => ({ ...prev, [id]: false }));
    }
  }, []);

  useEffect(() => {
    fetchAvailableTemplates();
  }, [fetchAvailableTemplates]);

  // The selected template id(s) — campaignTemplateIds is the source of truth,
  // falling back to the single campaignTemplateId.
  const selectedTemplateIds = useMemo(
    () =>
      toNumberArray(
        data.campaignTemplateIds && data.campaignTemplateIds.length
          ? data.campaignTemplateIds
          : data.campaignTemplateId
      ),
    [data.campaignTemplateIds, data.campaignTemplateId]
  );

  // Fetch a preview for every selected template that hasn't been fetched yet.
  useEffect(() => {
    selectedTemplateIds.forEach((id) => {
      if (!templatePreviewMap[id] && !templatePreviewLoadingIds[id]) {
        fetchTemplatePreview(id);
      }
    });
  }, [selectedTemplateIds, templatePreviewMap, templatePreviewLoadingIds, fetchTemplatePreview]);

  // Toggle a template in/out of the selection. Multiple selected → random mode
  // (payload uses campaignTemplateIds); a single one → single mode
  // (payload uses campaignTemplateId).
  const toggleTemplate = (templateId) => {
    if (isPreviewLocked) return;
    const id = toNumber(templateId);
    if (id == null) return;
    setData((d) => {
      const current = toNumberArray(
        d.campaignTemplateIds && d.campaignTemplateIds.length ? d.campaignTemplateIds : d.campaignTemplateId
      );
      const exists = current.some((x) => Number(x) === id);
      const nextIds = exists ? current.filter((x) => Number(x) !== id) : [...current, id];
      return {
        ...d,
        campaignTemplateIds: nextIds,
        campaignTemplateId: nextIds.length ? nextIds[0] : null,
        single: nextIds.length <= 1,
        random: nextIds.length > 1,
      };
    });
  };

  // Dropdown options: the campaign's own template(s) merged with the filtered
  // selectable list, deduped — so the current selection is always present.
  const dropdownTemplates = useMemo(() => {
    const map = new Map();
    (templates || []).forEach((tpl) => {
      const id = toNumber(tpl?.id);
      if (id != null && !map.has(id)) map.set(id, { id, name: tpl.name || `Template ${id}` });
    });
    availableTemplates.forEach((tpl) => {
      if (tpl.id != null && !map.has(tpl.id)) map.set(tpl.id, tpl);
    });
    return Array.from(map.values());
  }, [templates, availableTemplates]);

  const filteredDropdownTemplates = useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return dropdownTemplates;
    return dropdownTemplates.filter((tpl) =>
      String(tpl.name || "").toLowerCase().includes(query)
    );
  }, [dropdownTemplates, templateSearch]);

  // Label shown on the dropdown trigger: single template name, or "N selected".
  const templateTriggerLabel = useMemo(() => {
    if (!selectedTemplateIds.length) return "";
    if (selectedTemplateIds.length === 1) {
      const id = selectedTemplateIds[0];
      const match = dropdownTemplates.find((t) => Number(t.id) === Number(id));
      return match?.name || templatePreviewMap[id]?.name || `Template ${id}`;
    }
    return `${selectedTemplateIds.length} templates selected`;
  }, [selectedTemplateIds, dropdownTemplates, templatePreviewMap]);

  const openImagePreview = (src, title) => {
    if (src) setExpandedImage({ kind: "image", src, title });
  };
  const openHtmlPreview = (html, title) => {
    if (html) setExpandedImage({ kind: "html", html, title });
  };

  // Renders an Email / Landing / Awareness preview card for a given template id.
  // Prefers the rendered thumbnail image; if that image is absent or fails to
  // load it falls back to the compiled HTML (shown both as the thumbnail and in
  // the expanded view).
  const renderPreviewCard = (templateId, { key, title, imgSrc, html }) => {
    const fallback = imageFallback[templateId] || {};
    const loading = !!templatePreviewLoadingIds[templateId];
    const showImage = !!imgSrc && !fallback[key];
    const showHtml = !showImage && !!html;
    const clickable = showImage || showHtml;
    const landingUrl = templatePreviewMap[templateId]?.landingUrl;

    const handleClick = () => {
      if (showImage) openImagePreview(imgSrc, title);
      else if (showHtml) openHtmlPreview(html, title);
    };

    return (
      <div className="ec-template-preview-card" key={`${templateId}-${key}`}>
        <span className="ec-template-preview-title">{title}</span>
        <div
          className={`ec-template-preview-thumb${clickable ? " clickable" : ""}`}
          onClick={handleClick}
          title={clickable ? "Click to expand" : undefined}
        >
          {loading ? (
            <div className="ec-template-preview-spinner" />
          ) : showImage ? (
            <>
              <img
                src={imgSrc}
                alt={title}
                onError={() =>
                  setImageFallback((prev) => ({
                    ...prev,
                    [templateId]: { ...(prev[templateId] || {}), [key]: true },
                  }))
                }
              />
              <span className="ec-template-preview-expand">⤢</span>
            </>
          ) : showHtml ? (
            <>
              <HtmlThumb html={html} title={title} />
              <span className="ec-template-preview-expand">⤢</span>
            </>
          ) : (
            <img src={simulationandtrain} alt={title} />
          )}
        </div>
        {key === "landing" && landingUrl ? (
          <span className="ec-template-preview-url" title={landingUrl}>
            {landingUrl}
          </span>
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    if (!expandedImage) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setExpandedImage(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expandedImage]);

  useEffect(() => {
    if (activeTab >= visibleTabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, visibleTabs.length]);

  useEffect(() => {
    if (isPreviewLocked) return;
    if (!attackTypeOptions.length) return;
    const attackType = normalizeAttackType(data.attackVector);
    if (attackTypeOptions.some((option) => normalizeAttackType(option.value) === attackType)) return;
    setData((d) => applyAttackVectorDefaults(d, attackTypeOptions[0].value));
  }, [attackTypeOptions, data.attackVector, isPreviewLocked]);

  useEffect(() => {
    if (isPreviewLocked) return;
    if (!isTrainingSimulation) return;
    if (!awarenessTargetOptions.length) return;
    if (!awarenessTargetOptions.some((option) => option.value === data.lmsMsgStatus)) {
      setData((d) => ({ ...d, lmsMsgStatus: awarenessTargetOptions[0].value }));
    }
  }, [awarenessTargetOptions, data.lmsMsgStatus, isTrainingSimulation, isPreviewLocked]);

  const set = (key) => (val) => {
    if (isPreviewLocked) return;
    setData((d) => ({ ...d, [key]: val }));
  };

  const handleAttackVectorChange = (attackVector) => {
    if (isPreviewLocked) return;
    setData((d) => applyAttackVectorDefaults(d, attackVector));
  };

  const handleSimulationTypeChange = (simulationType) => {
    if (isPreviewLocked) return;
    const nextSimulationType = normalizeSimulationType(simulationType);
    const option = simulationTypeOptions.find((item) => item.value === nextSimulationType);
    setData((d) => {
      const next = { ...d, type: nextSimulationType };
      const nextAttackOptions = option?.attackTypeOptions || [];
      if (!nextAttackOptions.length) return next;

      const currentAttackType = normalizeAttackType(d.attackVector);
      const currentAttackIsValid = nextAttackOptions.some((attackOption) => normalizeAttackType(attackOption.value) === currentAttackType);
      return applyAttackVectorDefaults(next, currentAttackIsValid ? currentAttackType : nextAttackOptions[0].value);
    });
  };

  const removeTag = (idx) => {
    if (isPreviewLocked) return;
    setData((d) => {
      const nextGroupTags = d.groupTags.filter((_, i) => i !== idx);
      const nextGroupDetails = d.groupDetails.filter((_, i) => i !== idx);
      const nextGroupIds = nextGroupDetails.map((group) => group.id);

      return {
        ...d,
        groupTags: nextGroupTags,
        groupDetails: nextGroupDetails,
        groupIds: nextGroupIds,
        groups: String(nextGroupDetails.length || nextGroupTags.length),
        users: String(sumGroupTargets(nextGroupDetails)),
      };
    });
  };

  const fetchGroupOptions = useCallback(async () => {
    setGroupOptionsLoading(true);
    try {
      const res = await axios.get("/api/groups/parentGroup", {});
      const list = Array.isArray(res.data?.groups) ? res.data.groups : [];
      setGroupOptions(
        list.map((group) => ({
          id: Number(group.id),
          name: group.name ?? group.groupName ?? `Group ${group.id}`,
          targets: Number(group.targets) || 0,
        }))
      );
    } catch (err) {
      apiError(err);
      setGroupOptions([]);
    } finally {
      setGroupOptionsLoading(false);
    }
  }, []);

  const updateGroupPickerPosition = useCallback(() => {
    if (typeof window === "undefined") return;
    const anchor = groupPickerAnchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const width = Math.min(
      GROUP_PICKER_WIDTH,
      Math.max(GROUP_PICKER_MIN_WIDTH, viewportWidth - GROUP_PICKER_MARGIN * 2)
    );
    const left = clampNumber(
      rect.left,
      GROUP_PICKER_MARGIN,
      Math.max(GROUP_PICKER_MARGIN, viewportWidth - width - GROUP_PICKER_MARGIN)
    );

    const spaceBelow = viewportHeight - rect.bottom - GROUP_PICKER_MARGIN;
    const spaceAbove = rect.top - GROUP_PICKER_MARGIN;
    const shouldOpenAbove = spaceBelow < 260 && spaceAbove > spaceBelow;
    const availableHeight = shouldOpenAbove ? spaceAbove - 8 : spaceBelow - 8;
    const maxHeight = clampNumber(availableHeight, 220, GROUP_PICKER_ESTIMATED_HEIGHT);
    const top = shouldOpenAbove
      ? Math.max(GROUP_PICKER_MARGIN, rect.top - maxHeight - 8)
      : Math.min(rect.bottom + 8, viewportHeight - maxHeight - GROUP_PICKER_MARGIN);

    setGroupPickerStyle({
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      maxHeight: `${maxHeight}px`,
    });
  }, []);

  const openGroupPicker = () => {
    if (isPreviewLocked) return;
    const currentGroupDetails = data.groupDetails.length
      ? data.groupDetails
      : toNumberArray(data.groupIds).map((id, index) => {
          const option = groupOptions.find((group) => Number(group.id) === Number(id));
          const tagName = String(data.groupTags[index] || "")
            .replace(/\s*\(\s*\d+\s*Members\s*\)\s*$/, "")
            .trim();

          return {
            id,
            name: option?.name || tagName || `Group ${id}`,
            targets: Number(option?.targets) || 0,
          };
        });

    setGroupSearch("");
    setPendingGroupDetails(currentGroupDetails.map((group) => ({
      id: Number(group.id),
      name: group.name || `Group ${group.id}`,
      targets: Number(group.targets) || 0,
    })));
    setShowGroupPicker(true);
    if (groupOptions.length === 0) fetchGroupOptions();
  };

  const closeGroupPicker = () => {
    setShowGroupPicker(false);
    setGroupSearch("");
    setGroupPickerStyle(null);
  };

  useEffect(() => {
    if (!showGroupPicker) return undefined;

    updateGroupPickerPosition();
    const handleReposition = () => updateGroupPickerPosition();

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [showGroupPicker, updateGroupPickerPosition]);

  const filteredGroupOptions = useMemo(() => {
    const query = groupSearch.trim().toLowerCase();
    if (!query) return groupOptions;
    return groupOptions.filter((group) =>
      String(group.name).toLowerCase().includes(query)
    );
  }, [groupOptions, groupSearch]);

  const toggleGroupSelection = (group) => {
    if (isPreviewLocked) return;
    setPendingGroupDetails((current) => {
      const alreadySelected = current.some((existing) => Number(existing.id) === Number(group.id));
      if (alreadySelected) {
        return current.filter((existing) => Number(existing.id) !== Number(group.id));
      }

      return [
        ...current,
        {
          id: Number(group.id),
          name: group.name || `Group ${group.id}`,
          targets: Number(group.targets) || 0,
        },
      ];
    });
  };

  const applyPendingGroupSelection = () => {
    if (isPreviewLocked) return;
    const nextGroupDetails = pendingGroupDetails.map((group) => ({
      id: Number(group.id),
      name: group.name || `Group ${group.id}`,
      targets: Number(group.targets) || 0,
    }));
    const nextGroupIds = nextGroupDetails.map((group) => group.id);
    const nextGroupTags = nextGroupDetails.map(
      (group) => `${group.name} ( ${group.targets} Members )`
    );

    setData((d) => {
      return {
        ...d,
        groupIds: nextGroupIds,
        groupDetails: nextGroupDetails,
        groupTags: nextGroupTags,
        groups: String(nextGroupDetails.length),
        users: String(sumGroupTargets(nextGroupDetails)),
      };
    });
    closeGroupPicker();
  };

  const isUserReplyAllowed = useMemo(() => {
    const rules = getAttackTypeRules(data, attackTypeConditions);
    return rules.templateId !== false && isEmailTemplateFlow(data, attackTypeConditions, selectedAttackTypeOption);
  }, [attackTypeConditions, data, selectedAttackTypeOption]);

  useEffect(() => {
    if (isPreviewLocked) return;
    if (!isUserReplyAllowed && data.isRepliedCampaign) {
      setData((d) => ({ ...d, isRepliedCampaign: false }));
    }
  }, [data.isRepliedCampaign, isPreviewLocked, isUserReplyAllowed]);

  useEffect(() => {
    if (!isPreviewLocked) return;
    setShowGroupPicker(false);
    setPendingGroupDetails([]);
    setTemplatePickerOpen(false);
  }, [isPreviewLocked]);

  useEffect(() => {
    if (!templatePickerOpen) return;
    const handleMouseDown = (e) => {
      if (templateComboAnchorRef.current && !templateComboAnchorRef.current.contains(e.target)) {
        setTemplatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [templatePickerOpen]);

  const buildLaunchPayload = (overrides = {}) => {
    const scheduledAt =
      data.executionMode === "Schedule for later"
        ? toDate(overrides.scheduledAt || data.scheduledAt || new Date())
        : new Date();
    const attackType = normalizeAttackType(data.attackVector);
    const simulationType = normalizeSimulationType(data.type);
    const templateIds = toNumberArray(data.campaignTemplateIds.length ? data.campaignTemplateIds : data.campaignTemplateId);
    const random = !!data.random;
    const single = !random;
    const expiryOption = normalizeExpiryOption(data.expiryOption) || "7 Days";
    const resolvedCampaignExpiry = resolveCampaignExpiry({ ...data, expiryOption }, scheduledAt);
    const payload = {
      campaignName: (data.name || "").trim(),
      groupIds: toNumberArray(data.groupIds),
      frequency: "One Time",
      simulationType,
      category: data.category || undefined,
      scheduledAt,
      single,
      random,
      credentialHarvesting: coerceBoolean(data.credentialHarvesting, false),
      attackType,
      identifier: data.identifier || generateIdentifier(),
      sendCampaignOnOldSimulation: coerceBoolean(data.sendCampaignOnOldSimulation, false),
      simulationId: toNumber(data.simulationId) || 0,
      simulationState: data.simulationState || data.simulationState1 || null,
      simulationState1: data.simulationState1 || data.simulationState || null,
      simulationState2: data.simulationState2 || null,
      lmsMsgStatus: resolveLmsMsgStatus({ ...data, attackVector: attackType, type: simulationType }),
      delay: data.delay || 1,
      isRepliedCampaign: isUserReplyAllowed ? coerceBoolean(data.isRepliedCampaign, false) : false,
      isInstantAwareness: simulationType === "Simulation Attack" ? false : coerceBoolean(data.isInstantAwareness, true),
      campaignType: overrides.campaignType || data.campaignType || "live",
      expiryOption,
      campaignExpiry: resolvedCampaignExpiry,
      campaignPreviewId: data.campaignPreviewId || undefined,
      targetInteractionType: normalizeInteractionType(data.targetInteractionType) || undefined,
      isAIbased: data.isAIbased,
    };

    if (random) payload.campaignTemplateIds = templateIds;
    else payload.campaignTemplateId = templateIds[0];

    return cleanPayload(payload);
  };

  const validateLaunchPayload = (payload) => {
    const scheduledAt = toDate(payload.scheduledAt, null);
    const campaignExpiry = toDate(
      payload.campaignExpiry || resolveCampaignExpiry({ ...data, expiryOption: payload.expiryOption }, scheduledAt),
      null
    );

    if (data.launchStatus === "launched") {
      toast.error("This campaign preview has already been launched.");
      return false;
    }
    if (!payload.sendCampaignOnOldSimulation) {
      if (!payload.campaignName) {
        toast.error("Please enter campaign name");
        return false;
      }
      if (!payload.groupIds || payload.groupIds.length === 0) {
        toast.error("Please select groups");
        return false;
      }
    }
    if (payload.attackType !== "Removal Media") {
      if (payload.single && !payload.campaignTemplateId) {
        toast.error("Please select template");
        return false;
      }
      if (payload.random && (!payload.campaignTemplateIds || payload.campaignTemplateIds.length < 2)) {
        toast.error("Please select at least two templates for random mode");
        return false;
      }
    }
    if (payload.sendCampaignOnOldSimulation && (!payload.simulationId || !payload.simulationState1)) {
      toast.error("Please select follow-up campaign and result mode");
      return false;
    }
    if (payload.simulationType === "Simulation Attack + Training" && !payload.lmsMsgStatus) {
      toast.error("Please select awareness targeted group");
      return false;
    }
    if (!campaignExpiry || !scheduledAt || campaignExpiry <= scheduledAt) {
      toast.error("Campaign expiry date must be after the scheduled start date");
      return false;
    }
    return true;
  };

  const handleRunCampaign = (overrides = {}) => {
    const payload = buildLaunchPayload(overrides);
    if (!validateLaunchPayload(payload)) return;
    if (onRunCampaign) onRunCampaign(payload);
  };

  const handleViewLaunchedCampaign = () => {
    if (!launchedCampaignId) {
      toast.error("Launched campaign reference is not available.");
      return;
    }
    if (onViewCampaign) onViewCampaign(launchedCampaignId);
    else window.location.href = `/campaign/${launchedCampaignId}`;
  };

  return (
    <div className="ec-wrapper">

      {/* ── Tabs ── */}
      {/* <div className="ec-tabs">
        {visibleTabs.map((tab, i) => (
          <button
            key={tab}
            className={`ec-tab${activeTab === i ? " active" : ""}`}
            onClick={() => setActiveTab(i)}
          >
            {tab}
          </button>
        ))}
        <button className="ec-close-btn" onClick={onClose}>×</button>
      </div> */}

      {/* ── Training Tab (read-only courses for Simulation Attack + Training) ── */}
      {activeTabName === TRAINING_TAB && <AiTraining courseIds={data.courseIds} />}

      {activeTabName === PREVIEW_TAB && <>

      {/* ── Campaign Info ── */}
      <div className={`ec-info-section${isPreviewLocked ? " ec-readonly-section" : ""}`}>
        <div className="ec-info-name-row">
          <div>
            <span className="ec-info-label">Name</span>
            <InlineCampaignDraftField
              value={isHydrating ? "Loading..." : data.name}
              onChange={set("name")}
              disabled={isPreviewLocked || isHydrating}
            />
          </div>

          <button className="ec-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="ec-meta-row">
        <div className="ec-meta-item">
            <span className="ec-info-label">Type</span>
            <select
              className="ec-meta-select"
              value={data.type}
              disabled
              title="Simulation type is inherited from the selected template and can't be changed here"
              onChange={(e) => handleSimulationTypeChange(e.target.value)}
            >
              {visibleSimulationTypeOptions.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="ec-meta-item">
            <span className="ec-info-label">Attack Vector</span>
            <select
              className="ec-meta-select"
              value={data.attackVector}
              disabled
              title="Attack vector is inherited from the selected template and can't be changed here"
              onChange={(e) => handleAttackVectorChange(e.target.value)}
            >
              {visibleAttackTypeOptions.map((attackType) => (
                <option key={attackType.value} value={attackType.value}>{attackType.label}</option>
              ))}
            </select>
          </div>

          <div className="ec-meta-item">
            <span className="ec-info-label">Users</span>
            <span className="ec-info-value">{data.users}</span>
          </div>
          <div className="ec-meta-item">
            <span className="ec-info-label">Groups</span>
            <span className="ec-info-value">{data.groups}</span>
          </div>
        </div>
      </div>

      {/* ── Group Tags ── */}
      <div className="ec-groups-section">
        <span className="ec-groups-label">Groups:</span>
        {data.groupTags.map((tag, i) => (
          <span className="ec-group-tag" key={`${tag}-${i}`}>
            {tag}
            <button
              className="ec-tag-remove"
              onClick={() => removeTag(i)}
              disabled={isPreviewLocked}
              title={isPreviewLocked ? "This campaign has been launched" : "Remove group"}
            >
              ×
            </button>
          </span>
        ))}
        <div className="ec-group-picker-anchor" ref={groupPickerAnchorRef}>
          <button
            type="button"
            className={`ec-add-group-btn${showGroupPicker ? " active" : ""}`}
            onClick={() => (showGroupPicker ? closeGroupPicker() : openGroupPicker())}
            disabled={isPreviewLocked}
            title={isPreviewLocked ? "This campaign has been launched" : "Add groups"}
          >
            +
          </button>

          {showGroupPicker && typeof document !== "undefined" && ReactDOM.createPortal(
            <>
              <div
                className="ec-group-picker-backdrop"
                onClick={closeGroupPicker}
              />
              <div className="ec-group-picker" role="dialog" aria-label="Select groups" style={groupPickerStyle || undefined}>
                <div className="ec-group-picker-header">
                  <span className="ec-group-picker-title">Select Groups</span>
                  <button
                    type="button"
                    className="ec-group-picker-close"
                    onClick={closeGroupPicker}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <div className="ec-group-picker-search">
                  <PiMagnifyingGlass />
                  <input
                    type="search"
                    value={groupSearch}
                    autoFocus
                    placeholder="Search groups"
                    aria-label="Search groups"
                    onChange={(e) => setGroupSearch(e.target.value)}
                    disabled={isPreviewLocked}
                  />
                </div>

                <div className="ec-group-picker-list">
                  {groupOptionsLoading ? (
                    <div className="ec-group-picker-empty">Loading groups…</div>
                  ) : filteredGroupOptions.length === 0 ? (
                    <div className="ec-group-picker-empty">
                      {groupSearch.trim() ? "No matching groups." : "No groups found."}
                    </div>
                  ) : (
                    filteredGroupOptions.map((group) => {
                      const checked = pendingGroupDetails.some(
                        (pendingGroup) => Number(pendingGroup.id) === Number(group.id)
                      );
                      return (
                        <label
                          key={group.id}
                          className={`ec-group-picker-item${checked ? " selected" : ""}${isPreviewLocked ? " disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleGroupSelection(group)}
                            disabled={isPreviewLocked}
                          />
                          <span className="ec-group-picker-item-name" title={group.name}>
                            {group.name}
                          </span>
                          <span className="ec-group-picker-item-count">
                            {group.targets} {group.targets === 1 ? "user" : "users"}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="ec-group-picker-footer">
                  <span className="ec-group-picker-selected-count">
                    {pendingGroupDetails.length} selected
                  </span>
                  <button
                    type="button"
                    className="ec-group-picker-done"
                    onClick={applyPendingGroupSelection}
                  >
                    Done
                  </button>
                </div>
              </div>
            </>,
            document.body
          )}
        </div>
      </div>


      {/* ── Vishing Mode Selector ── */}
      {data.attackVector === "Vishing" && (() => {
        const isIVRSelected = normalizeInteractionType(data.targetInteractionType) === "outbound" ||
          (!normalizeInteractionType(data.targetInteractionType) && !coerceBoolean(data.isAIbased, false));
        const isAISelected = normalizeInteractionType(data.targetInteractionType) === "inbound";
        return (
          <div className="vishing-options-wrapper ec-vishing-wrapper">
            <div className="row">
              <div className="col-md-6 mb-3">
                <div
                  className={`vishing-card${isIVRSelected ? " active" : ""}${(isPreviewLocked || isAISelected) ? " disabled" : ""}`}
                  onClick={() => {
                    if (!isPreviewLocked && !isAISelected) setData(d => ({ ...d, isAIbased: false, targetInteractionType: "outbound" }));
                  }}
                  aria-disabled={isPreviewLocked || isAISelected}
                >
                  <div className="vishing-img-wrapper">
                    <img src={vishingImg} alt="IVR" />
                  </div>
                  <div className="vishing-content">
                    <div className="vishing-title">IVR (Keypad-based)</div>
                    <div className="vishing-desc">Automated pre-recorded calls that attempt to extract information using keypad inputs</div>
                  </div>
                  <div className="vishing-radio">
                    <input type="radio" checked={isIVRSelected} readOnly disabled={isPreviewLocked || isAISelected} />
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div
                  className={`vishing-card${isAISelected ? " active" : ""}${(isPreviewLocked || isIVRSelected) ? " disabled" : ""}`}
                  onClick={() => {
                    if (!isPreviewLocked && !isIVRSelected) setData(d => ({ ...d, isAIbased: true, targetInteractionType: "inbound" }));
                  }}
                  aria-disabled={isPreviewLocked || isIVRSelected}
                >
                  <div className="vishing-img-wrapper">
                    <img src={aivishingImg} alt="AI" />
                  </div>
                  <div className="vishing-content">
                    <div className="vishing-title">AI Conversational (Voice Bot)</div>
                    <div className="vishing-desc">Human-like AI voice interaction that dynamically engages users to extract sensitive information.</div>
                  </div>
                  <div className="vishing-radio">
                    <input type="radio" checked={isAISelected} readOnly disabled={isPreviewLocked || isIVRSelected} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── WhatsApp Mode Selector ── */}
      {data.attackVector === "WhatsApp" && (() => {
        const isAISelected = normalizeInteractionType(data.targetInteractionType) === "inbound" && coerceBoolean(data.isAIbased, false);
        const isNormalSelected = !isAISelected;
        return (
          <div className="vishing-options-wrapper ec-vishing-wrapper">
            <h5 className="ec-vishing-section-title">WhatsApp Phishing Mode</h5>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div
                  className={`vishing-card${isNormalSelected ? " active" : ""}${(isPreviewLocked || isAISelected) ? " disabled" : ""}`}
                  onClick={() => {
                    if (!isPreviewLocked && !isAISelected) setData(d => ({ ...d, isAIbased: false, targetInteractionType: "outbound" }));
                  }}
                  aria-disabled={isPreviewLocked || isAISelected}
                >
                  <div className="vishing-img-wrapper">
                    <img src={whatsappImgIVR} alt="WhatsApp" />
                  </div>
                  <div className="vishing-content">
                    <div className="vishing-title">Direct WhatsApp Message</div>
                    <div className="vishing-desc">Send tracked WhatsApp messages from the configured messaging gateway.</div>
                  </div>
                  <div className="vishing-radio">
                    <input type="radio" checked={isNormalSelected} readOnly disabled={isPreviewLocked || isAISelected} />
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div
                  className={`vishing-card${isAISelected ? " active" : ""}${(isPreviewLocked || isNormalSelected) ? " disabled" : ""}`}
                  onClick={() => {
                    if (!isPreviewLocked && !isNormalSelected) setData(d => ({ ...d, isAIbased: true, targetInteractionType: "inbound" }));
                  }}
                  aria-disabled={isPreviewLocked || isNormalSelected}
                >
                  <div className="vishing-img-wrapper">
                    <img src={whatsappImgChatbox} alt="AI WhatsApp" />
                  </div>
                  <div className="vishing-content">
                    <div className="vishing-title">AI Conversational (Chat Bot)</div>
                    <div className="vishing-desc">Send an email with a tracked WhatsApp link, then continue the interaction in AI chat.</div>
                  </div>
                  <div className="vishing-radio">
                    <input type="radio" checked={isAISelected} readOnly disabled={isPreviewLocked || isNormalSelected} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Settings ── */}
      <AiConfiguration
        userReply={data.isRepliedCampaign}
        disabled={isPreviewLocked || !isUserReplyAllowed}
        onUserReplyChange={(value) => set("isRepliedCampaign")(value)}
        showCredentialHarvesting={isCredentialHarvestingAllowed}
        credentialHarvesting={data.credentialHarvesting}
        onCredentialHarvestingChange={(value) => set("credentialHarvesting")(value)}
        credentialHarvestingDisabled
      />
      {isTrainingSimulation && (
        <TrainingSettings
          data={data}
          setData={setData}
          awarenessTargetOptions={awarenessTargetOptions}
          disabled={isPreviewLocked}
        />
      )}
      <AiCampaignExpiry
        selectedOption={data.expiryOption}
        customDate={data.customExpiryDate}
        scheduledAt={data.scheduledAt}
        disabled={isPreviewLocked}
        onChange={({ option, customDate, campaignExpiry }) => {
          if (isPreviewLocked) return;
          setData((d) => ({
            ...d,
            expiryOption: option,
            customExpiryDate: customDate,
            campaignExpiry,
          }));
        }}
      />
      <AiExecutionMode
        selectedMode={data.executionMode}
        scheduleDate={data.scheduledAt}
        disabled={isPreviewLocked}
        onChange={({ mode, scheduledAt, campaignType }) => {
          if (isPreviewLocked) return;
          const nextScheduledAt = scheduledAt || new Date();
          setData((d) => ({
            ...d,
            executionMode: mode,
            scheduledAt: nextScheduledAt,
            campaignType,
          }));
        }}
      />

      {/* ── Selected Template: dropdown (filtered by attack type / credential
           harvesting / sim type) + email / landing / awareness previews ── */}
      <div className="ec-selected-templates">
        <div className="ec-template-picker-head">
          <span className="ec-selected-templates-label">Selected Template:</span>
          <div className="ec-template-combo-anchor" ref={templateComboAnchorRef}>
            <button
              type="button"
              className={`ec-template-combo-trigger${templatePickerOpen ? " open" : ""}`}
              disabled={isPreviewLocked || (availableTemplatesLoading && dropdownTemplates.length === 0)}
              onClick={() => setTemplatePickerOpen((open) => !open)}
            >
              <span className={`ec-template-combo-value${templateTriggerLabel ? "" : " placeholder"}`}>
                {templateTriggerLabel || "Select template(s)"}
              </span>
              <span className="ec-template-combo-caret">▾</span>
            </button>

            {templatePickerOpen && (
              <>
                <div className="ec-template-combo-backdrop" />
                <div className="ec-template-combo-panel" role="dialog" aria-label="Select templates">
                  <div className="ec-template-combo-search">
                    <PiMagnifyingGlass />
                    <input
                      type="search"
                      ref={el => { if (el && !el.dataset.didFocus) { el.dataset.didFocus = "1"; el.focus({ preventScroll: true }); } }}
                      value={templateSearch}
                      placeholder="Search templates"
                      aria-label="Search templates"
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      disabled={isPreviewLocked}
                    />
                  </div>
                  <div className="ec-template-combo-list">
                    {availableTemplatesLoading && dropdownTemplates.length === 0 ? (
                      <div className="ec-template-combo-empty">Loading templates…</div>
                    ) : filteredDropdownTemplates.length === 0 ? (
                      <div className="ec-template-combo-empty">
                        {templateSearch.trim() ? "No matching templates." : "No templates found."}
                      </div>
                    ) : (
                      filteredDropdownTemplates.map((t) => {
                        const checked = selectedTemplateIds.some((id) => Number(id) === Number(t.id));
                        return (
                          <label
                            key={t.id}
                            className={`ec-template-combo-item${checked ? " selected" : ""}${isPreviewLocked ? " disabled" : ""}`}
                            title={t.name}
                          >
                            <input
                              type="checkbox"
                              className="ec-template-combo-checkbox"
                              checked={checked}
                              onChange={() => toggleTemplate(t.id)}
                              disabled={isPreviewLocked}
                            />
                            <span className="ec-template-combo-item-name">{t.name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div className="ec-template-combo-footer">
                    <span className="ec-template-combo-count">{selectedTemplateIds.length} selected</span>
                    <button
                      type="button"
                      className="ec-template-combo-done"
                      onClick={() => {
                        setTemplatePickerOpen(false);
                        setTemplateSearch("");
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {availableTemplatesLoading && availableTemplates.length === 0 ? (
          <div className="ec-template-preview-empty">Loading templates…</div>
        ) : selectedTemplateIds.length === 0 ? (
          <div className="ec-template-preview-empty">
            {availableTemplates.length
              ? "Select one or more templates to preview them."
              : "No templates match the current attack type / settings."}
          </div>
        ) : (
          <div className="ec-template-preview-list">
            {selectedTemplateIds.map((id) => {
              const preview = templatePreviewMap[id] || {};
              const name =
                preview.name ||
                dropdownTemplates.find((t) => Number(t.id) === Number(id))?.name ||
                `Template ${id}`;
              return (
                <div className="ec-template-preview-block" key={id}>
                  {selectedTemplateIds.length > 1 && (
                    <div className="ec-template-preview-block-head">
                      <span className="ec-template-preview-block-name" title={name}>{name}</span>
                      <button
                        type="button"
                        className="ec-template-preview-remove"
                        onClick={() => toggleTemplate(id)}
                        title="Remove this template"
                        aria-label="Remove this template"
                        disabled={isPreviewLocked}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div className="ec-template-preview-grid">
                    {showEmailPreview &&
                      renderPreviewCard(id, {
                        key: "email",
                        title: "Email Template",
                        imgSrc: preview.templatePath,
                        html: preview.emailHtml,
                      })}

                    {showLandingPreview &&
                      renderPreviewCard(id, {
                        key: "landing",
                        title: "Landing Page",
                        imgSrc: preview.pagePath,
                        html: preview.landingHtml,
                      })}

                    {showAwarenessPreview &&
                      renderPreviewCard(id, {
                        key: "awareness",
                        title: "Awareness Page",
                        // Compiled HTML fallback only applies when the awareness
                        // page is authored in the editor (custom-url has no HTML).
                        imgSrc: preview.awarenessPath,
                        html: preview.awarenessMode === "editor" ? preview.awarenessHtml : "",
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer Buttons ── */}
      <div className="ec-footer">
        {isAlreadyLaunched ? (
          <button
            className="ec-run-btn"
            onClick={handleViewLaunchedCampaign}
            disabled={isHydrating || !launchedCampaignId}
            title={
              launchedCampaignId
                ? "This campaign has been launched — open it"
                : "This campaign preview has already been launched."
            }
          >
            Navigate to Campaign
          </button>
        ) : (
          <button
            className="ec-run-btn"
            onClick={() => handleRunCampaign()}
            disabled={isRunningCampaign || isHydrating}
          >
            {isRunningCampaign ? "Launching..." : "Run Campaign"}
          </button>
        )}
      </div>

      </>}

      {/* ── Image lightbox (portaled to body so it overlays the whole viewport) ── */}
      {expandedImage &&
        ReactDOM.createPortal(
          <div
            className="ec-image-lightbox"
            role="dialog"
            aria-label={expandedImage.title || "Preview"}
            onClick={() => setExpandedImage(null)}
          >
            <div className="ec-image-lightbox-inner" onClick={(e) => e.stopPropagation()}>
              <div className="ec-image-lightbox-bar">
                <span className="ec-image-lightbox-title">{expandedImage.title}</span>
                <button
                  type="button"
                  className="ec-image-lightbox-close"
                  onClick={() => setExpandedImage(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="ec-image-lightbox-body">
                {expandedImage.kind === "html" ? (
                  <iframe
                    className="ec-image-lightbox-frame"
                    srcDoc={expandedImage.html}
                    title={expandedImage.title || "Preview"}
                    sandbox=""
                  />
                ) : (
                  <img src={expandedImage.src} alt={expandedImage.title || "Preview"} />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

    </div>
  );
};

export default CampaignDraftPreview;
